import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import GroupAssignBoard from '../../components/groups/GroupAssignBoard';

const AssignGroupInstructors = () => {
  const navigate  = useNavigate();
  const { groupId } = useParams();
  const location  = useLocation();

  const [groupName, setGroupName] = useState(location.state?.groupName || 'Group');
  const [assigned, setAssigned]   = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  const fetchGroupInstructors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.groups.getInstructors(groupId);
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load group instructors.');
        return;
      }
      const { group, assigned: assignedUsers, available: availableUsers } = response.data.data;
      if (group?.name) setGroupName(group.name);
      setAssigned(Array.isArray(assignedUsers) ? assignedUsers : []);
      setAvailable(Array.isArray(availableUsers) ? availableUsers : []);
    } catch (error) {
      toast.error(`Failed to load group instructors: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { fetchGroupInstructors(); }, [fetchGroupInstructors]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.groups.setInstructors(groupId, assigned.map((u) => u.id));
      toast.success('Instructor assignments saved successfully.');
      await fetchGroupInstructors();
    } catch (error) {
      toast.error(`Failed to save assignments: ${error.response?.data?.message || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96 p-4">
        <Spinner animation="border" className="w-12 h-12 text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/groups')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Assign Instructors — {groupName}
          </h5>
        </div>
        <Button
          bsPrefix="custom-hamburger"
          onClick={handleSave}
          disabled={saving}
          className="border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 px-6 py-2"
        >
          {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Assignments'}
        </Button>
      </div>

      <GroupAssignBoard
        assigned={assigned}
        available={available}
        setAssigned={setAssigned}
        setAvailable={setAvailable}
      />
    </div>
  );
};

export default AssignGroupInstructors;
